import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  IconButton,
  Alert,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

import { getExamById, getExamPapers, getPaperContent, deletePaper } from '../services/indianExamsService';
import { getUserAttempts } from '../services/paperAttemptService';
import { useAuth } from '../context/AuthContext';
import PaperCard from './PaperCard';
import AdminEditPaperModal from './admin/AdminEditPaperModal';
import { APP_DOWNLOAD } from '../config/appConfig';

export default function ExamDetailsModal({ open, examId, onClose }) {
  const navigate = useNavigate();

  const [exam, setExam] = useState(null);
  const [papers, setPapers] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Admin state
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [editModal, setEditModal] = useState({ open: false, paper: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, paper: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!open || !examId) return;

    setLoading(true);
    setError('');
    setExam(null);
    setPapers([]);
    setAttempts([]);
    setStatistics(null);

    async function loadExamAndPapers() {
      try {
        const examData = await getExamById(examId);
        setExam(examData);

        const papersData = await getExamPapers(examId);
        if (Array.isArray(papersData?.papers)) {
          setPapers(papersData.papers);
        } else if (Array.isArray(papersData)) {
          setPapers(papersData);
        } else {
          setPapers([]);
        }

        const attemptsData = await getUserAttempts({ examId, limit: 10 });
        if (attemptsData?.success && Array.isArray(attemptsData.attempts)) {
          setAttempts(attemptsData.attempts);
          setStatistics(calculateStatistics(attemptsData.attempts));
        }
      } catch (e) {
        console.error('[ExamDetailsModal] Error:', e);
        setError('Failed to load exam details');
      } finally {
        setLoading(false);
      }
    }

    loadExamAndPapers();
  }, [open, examId]);

  const calculateStatistics = (attemptsArray) => {
    if (!attemptsArray.length) {
      return {
        totalAttempts: 0,
        completedAttempts: 0,
        averageScore: 0,
        highestScore: 0,
        totalTimeSpent: 0,
        completionRate: 0
      };
    }

    const completed = attemptsArray.filter(a => a.status === 'completed');
    const scores = completed.map(a => a.score || 0).filter(Boolean);

    return {
      totalAttempts: attemptsArray.length,
      completedAttempts: completed.length,
      averageScore: scores.length
        ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
        : 0,
      highestScore: scores.length ? Math.max(...scores) : 0,
      totalTimeSpent: attemptsArray.reduce((s, a) => s + (a.timeElapsed || 0), 0),
      completionRate: Math.round((completed.length / attemptsArray.length) * 100)
    };
  };

  const handlePaperClick = (paper) => {
    (async () => {
      try {
        setLoading(true);
        // Fetch full paper content (questions) before starting
        const content = await getPaperContent(examId, paper.id);

        // Normalize questions from multiple possible backend shapes
        let questions = [];
        if (!content) {
          questions = [];
        } else if (Array.isArray(content.chunks)) {
          questions = content.chunks.flatMap(c => c.questions || []);
        } else if (content.paper && Array.isArray(content.paper.chunks)) {
          questions = content.paper.chunks.flatMap(c => c.questions || []);
        } else if (Array.isArray(content.questions)) {
          questions = content.questions;
        } else if (content.paper && Array.isArray(content.paper.questions)) {
          questions = content.paper.questions;
        } else if (Array.isArray(content.data)) {
          questions = content.data;
        } else if (content.chunks && Array.isArray(content.chunks[0]?.questions)) {
          questions = content.chunks.flatMap(c => c.questions || []);
        }

        const paperWithQuestions = { ...paper, questions };
        onClose();
        navigate(`/exam/${encodeURIComponent(examId)}/paper/${encodeURIComponent(paper.id)}`, { state: { exam, paper: paperWithQuestions, examId, paperId: paper.id } });
      } catch (e) {
        console.error('Failed to load paper content', e);
        setError('Failed to load paper content');
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleDeletePaper = async () => {
    if (!deleteModal.paper) return;
    setIsDeleting(true);
    try {
      const res = await deletePaper(examId, deleteModal.paper.id || deleteModal.paper.paperId);
      if (res && res.success) {
        setPapers(prev => prev.filter(p => (p.id || p.paperId) !== (deleteModal.paper.id || deleteModal.paper.paperId)));
      } else {
        alert(res?.error || 'Failed to delete paper');
      }
    } catch (e) {
      alert('Error deleting paper: ' + e.message);
    } finally {
      setIsDeleting(false);
      setDeleteModal({ open: false, paper: null });
    }
  };

  const handleSaveEdit = (updatedPaper) => {
    setPapers(prev => prev.map(p => (p.id || p.paperId) === (updatedPaper.id || updatedPaper.paperId) ? updatedPaper : p));
    setEditModal({ open: false, paper: null });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '80vh', maxHeight: '90vh', borderRadius: 2 } }}
    >
      {/* Header */}
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <SchoolIcon sx={{ fontSize: 32 }} />
          <Box flex={1}>
            <Typography variant="h5" fontWeight={700}>
              {exam?.name || 'Exam Details'}
            </Typography>
            {exam?.categoryName && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {exam.categoryName}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.2)' }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
            <CircularProgress size={48} />
            <Typography sx={{ mt: 2 }} color="text.secondary">
              Loading exam details...
            </Typography>
          </Stack>
        ) : error ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : !exam ? (
          <Box sx={{ p: 4 }}>
            <Typography textAlign="center" color="text.secondary">
              Exam not found.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 4, overflow: 'auto' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Previous Year Papers
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {papers.length} paper{papers.length !== 1 && 's'} available
            </Typography>

            {papers.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center', border: '2px dashed', bgcolor: 'grey.50' }}>
                <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="h6" mt={2}>
                  No Previous Papers Available
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {papers.map((paper, index) => {
                  const currentYear = new Date().getFullYear();
                  const paperYear = paper?.year ? Number(paper.year) : null;
                  const isPreviousYear = paperYear && paperYear < currentYear;
                  
                  const disabled = !!(paper.disabled || paper.readOnly || isPreviousYear);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={paper.id || index}>
                      <PaperCard
                        paper={paper}
                        index={index}
                        variant={isPreviousYear ? 'previous' : 'exam'}
                        onClick={() => { if (!disabled) handlePaperClick(paper); }}
                        showAppDownloadQr={APP_DOWNLOAD.enabled}
                        playStoreUrl={APP_DOWNLOAD.playStoreUrl}
                        qrImageUrl={APP_DOWNLOAD.qrImageUrl}
                        qrSize={APP_DOWNLOAD.qrSize}
                        disabled={disabled}
                        actionButtonText={disabled ? 'Unavailable' : 'Start Exam'}
                      />
                      {isAdmin && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="Edit Metadata">
                            <IconButton size="small" onClick={() => setEditModal({ open: true, paper })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Paper">
                            <IconButton size="small" color="error" onClick={() => setDeleteModal({ open: true, paper })}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Admin Delete Confirmation */}
      <Dialog open={deleteModal.open} onClose={() => !isDeleting && setDeleteModal({ open: false, paper: null })}>
        <DialogTitle>Delete Paper?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteModal.paper?.title}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModal({ open: false, paper: null })} disabled={isDeleting}>Cancel</Button>
          <Button onClick={handleDeletePaper} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Edit Modal */}
      {editModal.open && (
        <AdminEditPaperModal
          open={editModal.open}
          paper={editModal.paper}
          onClose={() => setEditModal({ open: false, paper: null })}
          onSave={handleSaveEdit}
        />
      )}
    </Dialog>
  );
}
